/**
 * What JMC's shared library holds, one shape per kind.
 *
 * The row stores `payload` as jsonb because a tone is one string and a persona
 * is six fields, and a table with every column of every kind would be mostly
 * nulls with no constraint worth writing (migration 089). The shape is enforced
 * here instead - at the edge, per kind - so an invalid persona is a 400 on the
 * screen that wrote it rather than a broken tile on fifty clinics' screens.
 *
 * IN `shared/` because three readers need the same definition: the JMC form that
 * writes one, the API that validates it, and the clinic screens that render it.
 */
import { Type, type Static } from '@sinclair/typebox';
import { OptionalText, Text } from './common.ts';

export const PlatformDefaultKind = Type.Union([
  Type.Literal('TONE'),
  Type.Literal('ARTICLE_TYPE'),
  Type.Literal('PERSONA'),
  Type.Literal('CTA'),
]);
export type PlatformDefaultKind = Static<typeof PlatformDefaultKind>;

/**
 * A tone, or an article type: one stored value and the label beside it.
 *
 * `value` is what lands on the article - the tone string handed to the model,
 * or `blog` / `column` / `case` - so it is NOT the label: changing how an option
 * reads must not rewrite what was chosen on articles already written.
 */
export const ChoicePayload = Type.Object(
  { value: Text(120) },
  { additionalProperties: false },
);

/** A starter persona. The same fields the clinic's own personas carry. */
export const PersonaPayload = Type.Object(
  {
    label: Text(100),
    /*
     * Genuinely optional, and `Type.Optional` rather than a zero-length string:
     * TypeBox requires every property of an object unless it is wrapped, so a
     * template carrying only a label - which is a perfectly good starting point -
     * was refused for having no 年代.
     */
    ageRange: Type.Optional(OptionalText(40)),
    gender: Type.Optional(OptionalText(40)),
    concerns: Type.Optional(OptionalText(2000)),
    tone: Type.Optional(OptionalText(200)),
    notes: Type.Optional(OptionalText(2000)),
    /*
     * The direction the model was given, carried into the copy.
     *
     * The clinic's persona screen has this field and the template did not, so an
     * adopted persona arrived with the one thing a re-draw needs missing - and
     * 「AIで下書き」 on the clinic side started from nothing.
     */
    aiBrief: Type.Optional(OptionalText(1000)),
  },
  { additionalProperties: false },
);

/**
 * A starter CTA.
 *
 * No `buttonUrl`: the destination is the one part of a call to action that is
 * the clinic's own, and a shared default carrying a URL is how somebody
 * publishes a booking link to another clinic's site.
 */
export const CtaPayload = Type.Object(
  {
    name: Text(120),
    heading: Type.Optional(OptionalText(200)),
    body: Type.Optional(OptionalText(1000)),
    buttonLabel: Type.Optional(OptionalText(60)),
    channels: Type.Optional(Type.Array(Text(40), { maxItems: 6 })),
  },
  { additionalProperties: false },
);

export type ChoicePayload = Static<typeof ChoicePayload>;
export type PersonaPayload = Static<typeof PersonaPayload>;
export type CtaPayload = Static<typeof CtaPayload>;

/** The validator for one kind, by name. */
export const PAYLOAD_SCHEMA = {
  TONE: ChoicePayload,
  ARTICLE_TYPE: ChoicePayload,
  PERSONA: PersonaPayload,
  CTA: CtaPayload,
} as const;

/** Which kinds are copied into a clinic rather than read live. */
export const COPIED_KINDS: PlatformDefaultKind[] = ['PERSONA', 'CTA'];

/** The table a copy of this kind lands in. */
export const COPY_TARGET: Partial<Record<PlatformDefaultKind, 'personas' | 'cta_templates'>> = {
  PERSONA: 'personas',
  CTA: 'cta_templates',
};

export const KIND_LABEL: Record<PlatformDefaultKind, string> = {
  TONE: '文体・トーン',
  ARTICLE_TYPE: '記事タイプ',
  PERSONA: 'ペルソナ雛形',
  CTA: 'CTA雛形',
};
